import type { StepFormStepId } from "@/api/service-provider/serviceProvider.endpoints";
import StepperFormLayout from "@/components/features/StepperFormLayout/StepperFormLayout";
import Loader from "@/components/ui/Loader/Loader";
import {
  useServiceProviderForm,
  useServiceProviderJsonForm,
} from "@/hooks/service-provider/useServiceProviderQueries";
import { generateDefaultValues } from "@/lib/generateDefaultValues";
import { buildZodSchemaFromJson } from "@/lib/jsonToZodSchema";
import { relaxRequiredFields } from "@/utils/relaxRequiredFields";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flex, MantineProvider } from "@mantine/core";
import {
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import {
  useServiceProviderOnboardingStore,
  type OnboardingCompanion,
} from "@/store/useServiceProviderOnboardingStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";

export const Route = createFileRoute("/service-providers/onboarding/_onboardingLayout")({
  component: OnboardingLayout,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function buildCleanDefaultsFromFields(fields: any[]): Record<string, any> {
  const cleanDefaults: Record<string, any> = {};
  fields.forEach((field: any) => {
    if (!field?.key) return;
    if (field.formControlType === "hidden") return;

    if (field.formControlType === "multiple_inputs") {
      const requiresInitialEntry = field.isRequired || (field.minItems && field.minItems > 0);
      if (!requiresInitialEntry) {
        cleanDefaults[field.key] = [];
        return;
      }
      const initialRow: Record<string, any> = { isDeleted: false };
      (field.fields ?? []).forEach((nf: any) => {
        if (!nf?.key) return;
        if (nf.formControlType === "hidden") {
          if (nf.autoGenerated && nf.type === "uuid") initialRow[nf.key] = null;
          return;
        }
        if (nf.formControlType === "mapAutoCompleteInput") {
          initialRow[nf.key] = null;
          initialRow[`${nf.key}_display`] = null;
          return;
        }
        if (nf.type === "boolean" || nf.formControlType === "checkbox") {
          initialRow[nf.key] = false;
          return;
        }
        initialRow[nf.key] = "";
      });
      cleanDefaults[field.key] = [initialRow];
    } else if (field.formControlType === "dayHours") {
      cleanDefaults[field.key] = [];
    } else if (field.formControlType === "mapAutoCompleteInput") {
      cleanDefaults[field.key] = null;
      cleanDefaults[`${field.key}_display`] = null;
    } else if (field.type === "boolean" || field.formControlType === "checkbox") {
      cleanDefaults[field.key] = false;
    } else {
      cleanDefaults[field.key] = "";
    }
  });
  return cleanDefaults;
}

function applyCompanionPrefill(
  values: Record<string, any>,
  companion: OnboardingCompanion,
): Record<string, any> {
  const next = { ...values };
  if (companion.personalInfoFirstName) {
    next.personalInfoFirstName = companion.personalInfoFirstName;
  }
  if (companion.personalInfoLastName) {
    next.personalInfoLastName = companion.personalInfoLastName;
  }
  if (companion.personalInfoPhone) {
    const existingPhones = Array.isArray(next.personalInfoPhone) ? next.personalInfoPhone : [];
    const [firstEntry, ...rest] = existingPhones;
    next.personalInfoPhone = [
      {
        ...(firstEntry ?? { isDeleted: false }),
        personalInfoPhonesPhone: companion.personalInfoPhone,
      },
      ...rest,
    ];
  }
  return next;
}

function OnboardingLayout() {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { stepId: StepFormStepId };
  const stepId = params.stepId as StepFormStepId;
  const location = useLocation();

  const { serviceProviderId, selectedCompanion } = useServiceProviderOnboardingStore();
  const isCompleteOnboardingRoute = location.pathname.includes("/complete-onboarding");

  const pathMatch = location.pathname.match(/\/service-providers\/onboarding\/([^/]+)\/([^/]+)/);
  const providerType =
    pathMatch && !["complete-onboarding", "summary"].includes(pathMatch[1]) ? pathMatch[1] : null;

  const {
    data: serviceProviderForm,
    isLoading,
    error,
  } = useServiceProviderForm(providerType || undefined);

  const {
    data: serviceProviderFormStepData,
    isLoading: isStepLoading,
    error: isStepError,
  } = useServiceProviderJsonForm(
    isCompleteOnboardingRoute ? (undefined as any) : (stepId as StepFormStepId),
    serviceProviderId ?? (null as any),
  );

  const firstStep = serviceProviderForm?.steps?.find((step) => step.stepNumber === 1);

  const isValidStepRoute = useMemo(() => {
    if (!serviceProviderForm || !serviceProviderForm.steps) return false;

    const isBaseRoute =
      location.pathname === "/service-providers/onboarding" ||
      location.pathname === "/service-providers/onboarding/";

    if (isBaseRoute) return false;

    const stepKeys = serviceProviderForm.steps.map((step) => step.stepKey);
    const isStepRoute = stepKeys.some((stepKey) => {
      if (providerType) {
        return (
          location.pathname === `/service-providers/onboarding/${providerType}/${stepKey}` ||
          location.pathname.includes(`/${providerType}/${stepKey}`)
        );
      }
      return false;
    });

    const isSummaryRoute = location.pathname.includes("/summary");

    return isStepRoute || isSummaryRoute;
  }, [serviceProviderForm, location.pathname, providerType]);

  useEffect(() => {
    if (isCompleteOnboardingRoute) return;

    if (firstStep && !isValidStepRoute) {
      if (providerType) {
        navigate({
          to: "/service-providers/onboarding/$providerType/$stepId",
          params: { providerType, stepId: firstStep.stepKey },
          replace: true,
        });
      } else {
        navigate({
          to: "/service-providers",
          replace: true,
        });
      }
    }
  }, [firstStep, navigate, providerType, isValidStepRoute, isCompleteOnboardingRoute]);

  const { schema, defaultValues, allFields } = useMemo(() => {
    if (isCompleteOnboardingRoute || !serviceProviderForm || !serviceProviderForm.steps) {
      return { allFields: [], schema: null, defaultValues: {} };
    }

    const currentStep = serviceProviderForm.steps.find((step) => step.stepKey === stepId);

    const schemaFields = currentStep
      ? currentStep.sections?.flatMap(
          (section) => section.subSections?.flatMap((ss) => ss.fields) || [],
        ) || []
      : [];

    const apiFields = (
      serviceProviderFormStepData?.sections?.flatMap(
        (section: any) => section.subSections?.flatMap((ss: any) => ss.fields) || [],
      ) || []
    ).map((f: any) => ({ ...f, _fromApi: true }));

    const fields = [...schemaFields, ...apiFields];

    const mergedSchemaStructure = currentStep
      ? {
          steps: [
            {
              ...currentStep,
              sections:
                currentStep.sections?.map((section) => {
                  const apiSection = serviceProviderFormStepData?.sections?.find(
                    (s: any) => s.key === section.key,
                  );

                  return {
                    ...section,
                    subSections: [
                      ...(section.subSections || []),
                      ...(apiSection?.subSections || []),
                    ],
                  };
                }) || [],
            },
          ],
        }
      : { steps: [] };

    const zodSchema = buildZodSchemaFromJson(
      relaxRequiredFields(fields),
      relaxRequiredFields(mergedSchemaStructure),
    );

    // When the helper has just been chosen on /complete-onboarding and we land
    // on personal-info with no serviceProviderId yet, force a clean empty-default
    // set (ignoring any value the cached schema baked in from the parent SP) and
    // apply the companion's prefill on top. Once the helper submits personal-info
    // a serviceProviderId is set, so we drop back to the normal default path.
    let defaults: Record<string, any>;
    if (stepId === "personal-info" && selectedCompanion && !serviceProviderId) {
      defaults = applyCompanionPrefill(buildCleanDefaultsFromFields(fields), selectedCompanion);
    } else {
      defaults = generateDefaultValues(fields, mergedSchemaStructure);
    }

    return {
      allFields: fields,
      schema: zodSchema,
      defaultValues: defaults,
    };
  }, [
    serviceProviderForm,
    serviceProviderFormStepData,
    isCompleteOnboardingRoute,
    stepId,
    selectedCompanion,
    serviceProviderId,
  ]);

  const onBoardingFormSteps = useMemo(() => {
    if (!serviceProviderForm || !providerType || !serviceProviderForm.steps) return [];

    const formSteps = serviceProviderForm.steps.map((step) => ({
      id: String(step.stepNumber),
      label: step.stepTitle,
      url: `/service-providers/onboarding/${providerType}/${step.stepKey}`,
    }));

    return formSteps.concat({
      id: String(formSteps.length + 1),
      label: "Summary",
      url: `/service-providers/onboarding/${providerType}/summary`,
    });
  }, [serviceProviderForm, providerType]);

  if (isCompleteOnboardingRoute) {
    if (isLoading) return <FullPageLoader />;
    if (error) return <div>Error loading data</div>;
    return <Outlet />;
  }

  if (isLoading) return <FullPageLoader />;
  if (error) return <div>Error loading data</div>;
  if (!serviceProviderForm) return <div>No Data found</div>;

  return (
    <MantineProvider
      theme={{
        fontFamily: "var(--font-text)",
      }}
    >
      <OnboardingFormShell
        schema={schema}
        defaultValues={defaultValues}
        allFields={allFields}
        stepId={stepId}
        selectedCompanion={selectedCompanion}
        onBoardingFormSteps={onBoardingFormSteps}
        isStepError={!!isStepError}
        isStepLoading={isStepLoading}
      />
    </MantineProvider>
  );
}

interface OnboardingFormShellProps {
  schema: any;
  defaultValues: Record<string, any>;
  allFields: any[];
  stepId: StepFormStepId;
  selectedCompanion: OnboardingCompanion | null;
  onBoardingFormSteps: Array<{ id: string; label: string; url: string }>;
  isStepError: boolean;
  isStepLoading: boolean;
}

function OnboardingFormShell({
  schema,
  defaultValues,
  allFields,
  stepId,
  selectedCompanion,
  onBoardingFormSteps,
  isStepError,
  isStepLoading,
}: OnboardingFormShellProps) {
  const methods = useForm({
    resolver: schema ? zodResolver(schema) : undefined,
    defaultValues,
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const hasPrefilledFromCompanionRef = useRef(false);

  useEffect(() => {
    if (!defaultValues || Object.keys(defaultValues).length === 0) return;

    const isStartingCompanionOnboarding =
      stepId === "personal-info" && !!selectedCompanion && !hasPrefilledFromCompanionRef.current;

    // Once the companion's personal-info form has been initialized with the
    // clean defaults + prefill (via useForm's initial defaultValues + the first
    // reset below), don't reset again on this step — that would clobber what
    // the helper has typed. The next reset only needs to fire when stepId
    // changes (handled by the normal branch).
    const isAfterCompanionInit =
      stepId === "personal-info" && !!selectedCompanion && hasPrefilledFromCompanionRef.current;
    if (isAfterCompanionInit) return;

    const currentValues = methods.getValues();
    const mergedValues = { ...defaultValues };

    if (isStartingCompanionOnboarding) {
      // defaultValues already contains clean empty defaults + companion
      // prefill (set by the parent's useMemo). Wipe any leftover keys present
      // in the live form so reset has an explicit empty for them too.
      Object.keys(currentValues).forEach((key) => {
        if (key in mergedValues) return;
        const liveValue = currentValues[key];
        if (Array.isArray(liveValue)) mergedValues[key] = [];
        else if (typeof liveValue === "boolean") mergedValues[key] = false;
        else if (liveValue !== undefined && liveValue !== null) mergedValues[key] = "";
      });

      hasPrefilledFromCompanionRef.current = true;
      methods.reset(mergedValues);
      return;
    }

    Object.keys(currentValues).forEach((key) => {
      const field = allFields.find((f: any) => f.key === key);

      if (field?.formControlType === "multiple_inputs") {
        const shouldHaveInitialItem = field.isRequired || (field.minItems && field.minItems > 0);
        const currentValue = currentValues[key];
        const defaultValue = defaultValues[key];

        if (
          shouldHaveInitialItem &&
          Array.isArray(currentValue) &&
          currentValue.length > 0 &&
          (!Array.isArray(defaultValue) || defaultValue.length === 0)
        ) {
          mergedValues[key] = currentValue;
        }
      } else if (
        !(key in defaultValues) &&
        currentValues[key] !== undefined &&
        currentValues[key] !== null
      ) {
        mergedValues[key] = currentValues[key];
      }
    });

    methods.reset(mergedValues);
  }, [defaultValues, methods, allFields, stepId, selectedCompanion]);

  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === "hasMailingAddress" && !value.hasMailingAddress) {
        methods.clearErrors("mailingAddress");
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <StepperFormLayout headerLabel="New Service Provider" steps={onBoardingFormSteps}>
        {isStepError ? (
          <div>Unable to load step data. Please try again.</div>
        ) : isStepLoading ? (
          <Loader />
        ) : (
          <div style={{ paddingTop: 10 }}>
            <Outlet />
          </div>
        )}
      </StepperFormLayout>
    </FormProvider>
  );
}

function FullPageLoader() {
  return (
    <Flex align="center" justify="center" style={{ minHeight: "70vh", width: "100%" }}>
      <Loader />
    </Flex>
  );
}
